"use server";

import { v4 as uuidv4 } from "uuid";
import { Prisma } from "@/prisma/generated/client";

import { auth } from "@/auth";
import { GRP_ROLE_ADMIN } from "@/lib/grp";
import { requireRole } from "@/lib/permissions";
import { prisma } from "@/prisma";
import type { ActionResult } from "@/types/server-action";
import {
  addFacturationExclusionRuleSchema,
  deleteFacturationExclusionRuleSchema,
  editFacturationExclusionRuleSchema,
} from "@/lib/schemas/facturation-exclusion-rules";

function parseDateOnlyToDateStart(v: string) {
  return new Date(v + "T00:00:00");
}

function parseDateOnlyToDateEnd(v: string) {
  return new Date(v + "T23:59:59.999");
}

function buildOverlapWhere(params: {
  brandNumericCode: string;
  brandAlphabeticCode: string;
  saleNature: string;
  billingCode: "FDM" | "VN" | "FU" | "VO" | "DA" | "CS";
  effectiveStartDate: Date;
  effectiveEndDate: Date | null;
  excludeId?: number;
}): Prisma.FacturationExclusionRuleWhereInput {
  const {
    brandNumericCode,
    brandAlphabeticCode,
    saleNature,
    billingCode,
    effectiveStartDate,
    effectiveEndDate,
    excludeId,
  } = params;

  return {
    deletedAt: null,
    brandNumericCode,
    brandAlphabeticCode,
    saleNature,
    billingCode,
    ...(excludeId ? { NOT: { id: excludeId } } : {}),
    ...(effectiveEndDate
      ? { effectiveStartDate: { lte: effectiveEndDate } }
      : {}),
    OR: [
      { effectiveEndDate: null },
      { effectiveEndDate: { gte: effectiveStartDate } },
    ],
  };
}

async function ensureSaleNatureExists(saleNature: string) {
  const existingSaleNature = await prisma.aakRule.findFirst({
    where: {
      saleNature,
      deletedAt: null,
    },
    select: { id: true },
  });

  return !!existingSaleNature;
}

export async function addFacturationExclusionRule(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = addFacturationExclusionRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return {
      success: false,
      fieldErrors: parsed.error.flatten().fieldErrors as Record<
        string,
        string[]
      >,
    };
  }

  const {
    brandNumericCode,
    brandAlphabeticCode,
    brandAlphabeticLabel,
    saleNature,
    billingCode,
    effectiveStartDate,
    effectiveEndDate,
  } = parsed.data;

  const saleNatureExists = await ensureSaleNatureExists(saleNature);
  if (!saleNatureExists) {
    return {
      success: false,
      message: `La nature de vente "${saleNature}" n'existe pas dans le référentiel.`,
    };
  }

  const startDate = parseDateOnlyToDateStart(effectiveStartDate);
  const endDate = effectiveEndDate
    ? parseDateOnlyToDateEnd(effectiveEndDate)
    : null;

  const conflict = await prisma.facturationExclusionRule.findFirst({
    where: buildOverlapWhere({
      brandNumericCode,
      brandAlphabeticCode,
      saleNature,
      billingCode,
      effectiveStartDate: startDate,
      effectiveEndDate: endDate,
    }),
    select: { id: true },
  });

  if (conflict) {
    return {
      success: false,
      message:
        "Conflit : une règle active existe déjà sur cette période pour la même marque, nature de vente et code facturation.",
    };
  }

  await prisma.facturationExclusionRule.create({
    data: {
      brandNumericCode,
      brandAlphabeticCode,
      brandAlphabeticLabel,
      saleNature,
      billingCode,
      effectiveStartDate: startDate,
      effectiveEndDate: endDate,
      isActive: true,
      version: 1,
      versionGroupId: uuidv4(),
      versionNote: null,
      deletedAt: null,
    },
  });

  return { success: true };
}

export async function editFacturationExclusionRule(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = editFacturationExclusionRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return {
      success: false,
      fieldErrors: parsed.error.flatten().fieldErrors as Record<
        string,
        string[]
      >,
    };
  }

  const {
    id,
    brandNumericCode,
    brandAlphabeticCode,
    brandAlphabeticLabel,
    saleNature,
    billingCode,
    effectiveStartDate,
    effectiveEndDate,
    isActive,
    versionNote,
  } = parsed.data;

  const existing = await prisma.facturationExclusionRule.findFirst({
    where: { id, deletedAt: null },
  });

  if (!existing) {
    return { success: false, message: "Règle introuvable" };
  }

  const saleNatureExists = await ensureSaleNatureExists(saleNature);
  if (!saleNatureExists) {
    return {
      success: false,
      message: `La nature de vente "${saleNature}" n'existe pas dans le référentiel.`,
    };
  }

  const startDate = parseDateOnlyToDateStart(effectiveStartDate);
  const endDate = effectiveEndDate
    ? parseDateOnlyToDateEnd(effectiveEndDate)
    : null;

  const conflict = await prisma.facturationExclusionRule.findFirst({
    where: buildOverlapWhere({
      brandNumericCode,
      brandAlphabeticCode,
      saleNature,
      billingCode,
      effectiveStartDate: startDate,
      effectiveEndDate: endDate,
      excludeId: id,
    }),
    select: { id: true },
  });

  if (conflict) {
    return {
      success: false,
      message:
        "Conflit : une règle active existe déjà sur cette période pour la même marque, nature de vente et code facturation.",
    };
  }

  await prisma.$transaction([
    prisma.facturationExclusionRule.update({
      where: { id },
      data: { deletedAt: new Date(), isActive: false },
    }),
    prisma.facturationExclusionRule.create({
      data: {
        brandNumericCode,
        brandAlphabeticCode,
        brandAlphabeticLabel,
        saleNature,
        billingCode,
        effectiveStartDate: startDate,
        effectiveEndDate: endDate,
        isActive,
        version: existing.version + 1,
        versionGroupId: existing.versionGroupId,
        versionNote: versionNote ?? null,
        deletedAt: null,
      },
    }),
  ]);

  return { success: true };
}

export async function deleteFacturationExclusionRule(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = deleteFacturationExclusionRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return {
      success: false,
      fieldErrors: parsed.error.flatten().fieldErrors as Record<
        string,
        string[]
      >,
    };
  }

  const { id } = parsed.data;

  await prisma.facturationExclusionRule.update({
    where: { id },
    data: { deletedAt: new Date(), isActive: false },
  });

  return { success: true };
}
